/**
 * UI State Slice
 *
 * Manages UI-specific state including expanded sections, active tabs,
 * modals, toasts, and other ephemeral UI state.
 */

import type { StateCreator } from 'zustand';
import { generateUUID } from '../utils/uuid';

export type ViewMode = 'configuration' | 'results' | 'validation';
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

export interface UISlice {
  // View state
  currentView: ViewMode;
  setCurrentView: (view: ViewMode) => void;

  // Section expansion state
  expandedSections: Set<string>;
  toggleSection: (sectionId: string) => void;
  expandSection: (sectionId: string) => void;
  collapseSection: (sectionId: string) => void;
  expandAllSections: () => void;
  collapseAllSections: () => void;

  // Active tab state
  activeTab: string;
  setActiveTab: (tab: string) => void;

  // Modal state
  openModals: Set<string>;
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
  closeAllModals: () => void;
  isModalOpen: (modalId: string) => boolean;

  // Toast notifications
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Loading states
  loadingStates: Map<string, boolean>;
  setLoading: (key: string, loading: boolean) => void;
  isLoading: (key: string) => boolean;

  // Sidebar state
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Panel state
  rightPanelOpen: boolean;
  rightPanelContent: string | null;
  toggleRightPanel: () => void;
  setRightPanelContent: (content: string | null) => void;
  closeRightPanel: () => void;

  // Theme
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;

  // Show derivations
  showDerivations: boolean;
  toggleDerivations: () => void;
  setShowDerivations: (show: boolean) => void;
}

export const createUISlice: StateCreator<UISlice, [], [], UISlice> = (set, get) => ({
  // Initial state
  currentView: 'configuration',
  expandedSections: new Set(['compute', 'network-ew', 'network-ns', 'scale']),
  activeTab: 'summary',
  openModals: new Set(),
  toasts: [],
  loadingStates: new Map(),
  sidebarOpen: true,
  rightPanelOpen: false,
  rightPanelContent: null,
  theme: 'light',
  showDerivations: true,

  // View actions
  setCurrentView: (view) => set({ currentView: view }),

  // Section actions
  toggleSection: (sectionId) =>
    set((state) => {
      const expanded = new Set(state.expandedSections);
      if (expanded.has(sectionId)) {
        expanded.delete(sectionId);
      } else {
        expanded.add(sectionId);
      }
      return { expandedSections: expanded };
    }),

  expandSection: (sectionId) =>
    set((state) => {
      const expanded = new Set(state.expandedSections);
      expanded.add(sectionId);
      return { expandedSections: expanded };
    }),

  collapseSection: (sectionId) =>
    set((state) => {
      const expanded = new Set(state.expandedSections);
      expanded.delete(sectionId);
      return { expandedSections: expanded };
    }),

  expandAllSections: () =>
    set({
      expandedSections: new Set([
        'compute',
        'server',
        'network-ew',
        'network-ns',
        'network-mgmt',
        'rack',
        'power',
        'scale',
      ]),
    }),

  collapseAllSections: () =>
    set({ expandedSections: new Set() }),

  // Tab actions
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Modal actions
  openModal: (modalId) =>
    set((state) => {
      const modals = new Set(state.openModals);
      modals.add(modalId);
      return { openModals: modals };
    }),

  closeModal: (modalId) =>
    set((state) => {
      const modals = new Set(state.openModals);
      modals.delete(modalId);
      return { openModals: modals };
    }),

  closeAllModals: () => set({ openModals: new Set() }),

  isModalOpen: (modalId) => get().openModals.has(modalId),

  // Toast actions
  addToast: (toast) =>
    set((state) => {
      const id = generateUUID();
      const newToast = { ...toast, id };
      return { toasts: [...state.toasts, newToast] };
    }),

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearToasts: () => set({ toasts: [] }),

  // Loading actions
  setLoading: (key, loading) =>
    set((state) => {
      const loadingStates = new Map(state.loadingStates);
      if (loading) {
        loadingStates.set(key, true);
      } else {
        loadingStates.delete(key);
      }
      return { loadingStates };
    }),

  isLoading: (key) => get().loadingStates.get(key) || false,

  // Sidebar actions
  toggleSidebar: () =>
    set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  // Right panel actions
  toggleRightPanel: () =>
    set((state) => ({ rightPanelOpen: !state.rightPanelOpen })),

  setRightPanelContent: (content) =>
    set({
      rightPanelContent: content,
      rightPanelOpen: content !== null,
    }),

  closeRightPanel: () =>
    set({
      rightPanelOpen: false,
      rightPanelContent: null,
    }),

  // Theme actions
  toggleTheme: () =>
    set((state) => ({
      theme: state.theme === 'light' ? 'dark' : 'light',
    })),

  setTheme: (theme) => set({ theme }),

  // Derivation actions
  toggleDerivations: () =>
    set((state) => ({ showDerivations: !state.showDerivations })),

  setShowDerivations: (show) => set({ showDerivations: show }),
});
